import React from 'react';
import { Skeleton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import styles from './PlanChangeModal.module.css';

/** Peças visuais dos popups de troca de plano (upgrade, plano menor, encaixe). */

export const formatPrice = (price) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);

export const formatCents = (cents) => formatPrice((cents || 0) / 100);

/** 'YYYY-MM-DD' ou 'YYYY-MM-DD HH:MM:SS' -> 'DD/MM/AAAA', sem passar pelo Date (fuso). */
export const formatShortDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : null;
};

const MiniPlan = ({ label, plan, highlight }) => (
  <div className={`${styles.miniPlan} ${highlight ? styles.miniPlanNew : ''}`}>
    <span className={styles.miniPlanLabel}>{label}</span>
    <span className={styles.miniPlanName}>{plan?.name || 'Atual'}</span>
    <span className={styles.miniPlanPrice}>
      {formatPrice(plan?.price)}<span className={styles.miniPlanPer}>/mês</span>
    </span>
  </div>
);

/** "De -> para" em dois mini-cards: plano atual cinza, novo com a cor principal. */
export const PlanChangeComparison = ({ currentPlan, newPlan }) => (
  <div className={styles.comparison}>
    <MiniPlan label="Plano atual" plan={currentPlan} />
    <span className={styles.comparisonArrow} aria-hidden="true">
      <ArrowForwardIcon fontSize="inherit" />
    </span>
    <MiniPlan label="Novo plano" plan={newPlan} highlight />
  </div>
);

/** Lista curta do que passa a ter (gain) ou deixa de ter (loss). */
export const PlanChangeList = ({ title, items, tone = 'gain' }) => {
  if (!items || items.length === 0) return null;
  const Icon = tone === 'loss' ? RemoveIcon : CheckIcon;
  return (
    <div className={styles.listBlock}>
      <span className={styles.sectionLabel}>{title}</span>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item} className={styles.listItem}>
            <span className={`${styles.listIcon} ${tone === 'loss' ? styles.listIconLoss : ''}`}>
              <Icon fontSize="inherit" />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Quadro de valores: linhas { label, value, hint, tone } (tone 'success' pinta o
 * valor de verde). Com `loading`, Skeleton no lugar dos valores. `note`: texto
 * de rodapé do quadro.
 */
export const PlanChangeValues = ({ rows, loading = false, note = null }) => (
  <div className={styles.values}>
    {rows.map((row) => (
      <div key={row.label} className={styles.valueRow}>
        <div className={styles.valueText}>
          <span className={styles.valueLabel}>{row.label}</span>
          {row.hint && !loading && <span className={styles.valueHint}>{row.hint}</span>}
        </div>
        {loading ? (
          <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: '8px' }} />
        ) : (
          <span className={`${styles.valueAmount} ${row.tone === 'success' ? styles.valueAmountSuccess : ''}`}>
            {row.value}
          </span>
        )}
      </div>
    ))}
    {note && !loading && <p className={styles.valuesNote}>{note}</p>}
  </div>
);
